/**
 * RFC-160 Phase 2 — Context screens (lifted from the retired
 * ContextScopeV0Wizard). These feed the front half of the merged
 * `OnboardingWizard`: getting started, company context, remaining
 * infrastructure questions (the cloud + code-host scanning steps live in
 * `buildScopeScreens.ts` per the consolidation), audit scope, roles,
 * auditor. No review screen — the merged wizard emits a single final
 * review at the very end.
 */
import {
  ComplianceFramework,
  defaultAuditTypeForFramework,
  GDPR_SCOPE_TOKEN_LABELS,
  ONBOARDING_AUDIT_TYPE_OPTIONS,
  ONBOARDING_DATA_TYPE_OPTIONS,
  ONBOARDING_HIPAA_ENTITY_TYPE_OPTIONS,
  ONBOARDING_INDUSTRY_OPTIONS,
  ONBOARDING_INFRASTRUCTURE_MODEL_OPTIONS,
  ONBOARDING_PREVIOUS_PLATFORM_OPTIONS,
  ONBOARDING_TEAM_SIZE_OPTIONS,
  ONBOARDING_WORK_MODEL_OPTIONS,
  type ProviderServiceDTO,
} from '@deepguide-ai/types'
import { getFrameworkDisplayName } from '@/hooks/useComplianceFramework'
import type { WizardScreen, CloudProviderAnswer } from '@/components/screenata/TypeformWizard'

/**
 * Framework checkbox options, in display order. Gated frameworks
 * (`FRAMEWORK_ENROLLMENT_FLAGS`) appear only when the caller reports their
 * flag on — the server rejects enrolling them otherwise, so offering them
 * unconditionally would be an option that 403s.
 *
 * `OTHER` stays last as the catch-all for frameworks we don't model.
 */
// RFC-231: the picker moved to the create-workspace form; canonical copy lives in lib.
import { frameworkOptions } from '@/lib/compliance-framework'
export { frameworkOptions }

/**
 * Screen ids this builder can produce. The Setup guide deep-links a question
 * with `?step=<key>`, and a key that is not a screen here resolves to the
 * first unanswered screen instead — a click that silently goes somewhere
 * else. Consumers check membership before offering the link.
 *
 * The remaining tooling questions
 * (`ticketingPlatforms`, `databaseProviders`, `monitoringTools`,
 * `vulnScanningTools`, `aiProviders`) have no wizard screen at all.
 */
const CONTEXT_SCREEN_KEYS = new Set<string>([
  'auditorFirmName',
  'companyDescription',
  'companyName',
  'complianceRoles',
  'controlMatrixFile',
  'dataRegions',
  'dataTypes',
  'environments',
  'founderInfo',
  'foundingDate',
  'frameworks',
  'gdprBases',
  'gdprRoles',
  'hasControlMatrix',
  'headquarters',
  'hipaaEntityType',
  'industry',
  'infrastructureModel',
  'noPhiAcknowledgment',
  'previousCompliancePlatform',
  'productNames',
  'teamSize',
  'trustServiceCriteria',
  'workModel',
])

export function buildContextScreens(
  firstName?: string,
  _providerServices?: Record<string, ProviderServiceDTO[]>,
  framework = 'SOC2',
  /** Feature-flag snapshot, keyed by flag — gates ISO 27001 / ISO 42001. */
  enabledFlags: Record<string, boolean> = {},
): WizardScreen[] {
  const greeting = firstName ? `Hi ${firstName}, I'm Vera 👋` : "Hi, I'm Vera 👋"
  const frameworkName = getFrameworkDisplayName(framework)
  return [
    // ── Welcome ────────────────────────────────────────────────────────
    {
      id: 'welcome',
      section: 'Getting started',
      // RFC-164: framework-agnostic copy. The user picks one or more
      // frameworks later; we don't assume SOC 2 here.
      // Warm-first structure: the greeting is the headline, the scan-first
      // promise is a second header (meta.subheading — the welcome renderer
      // reads it; other screen types ignore meta).
      question: greeting,
      explanation: `I'm your AI compliance officer. I'll help you get through audit prep, end to end.\n\nNew to this? Here's our {{first-timer's guide|https://screenata.com/resources/blog/soc-2-for-first-timers-what-to-read-what-to-skip-and-what-to-tell-your-ceo}}.\n\nHit continue and we'll connect your first tool. If you get stuck, {{Chat with Vera}} anytime.`,
      meta: {
        // Matches the real first screens: `websiteScan` (public pages, no
        // credentials) comes before the identity/cloud/code connect steps.
        subheading: "Let's start with your website, then your stack",
        subtext:
          "First Vera reads your public pages for company details (no credentials needed). Next you connect identity, cloud, and code with read-only access, and in minutes you'll see what already passes, what doesn't, and where the gaps are. From there we turn those gaps into a plan: policies, evidence, and an auditor-ready program.",
      },
      type: 'welcome',
      required: false,
    },
    // ── Frameworks (multi-select) ─────────────────────────────────────
    // RFC-160: audit programs are multi-framework. A company may run
    // SOC 2 + HIPAA in parallel; each gets its own AuditCycle row. The
    // per-framework audit type lives on subsequent screens, each gated
    // via `skipWhen` to the framework being selected.
    {
      id: 'frameworks',
      section: 'Getting started',
      question: 'Which compliance frameworks are you working toward?',
      explanation:
        'Pick every framework your customers or auditors are asking for. Each one gets its own audit: same company, multiple tracks. Vera will seed controls for each and dedupe shared evidence.',
      suggestedAnswer: 'SOC 2 is the most common starting point for B2B SaaS.',
      type: 'checkbox',
      required: true,
      options: frameworkOptions(enabledFlags),
    },
    // ── GDPR enrollment scope (RFC-222 §6) ────────────────────────────
    // Both screens are REQUIRED when GDPR is selected: the final save
    // activates the enrollment, and the server rejects a GDPR activation
    // without at least one role and one territorial basis.
    {
      id: 'gdprRoles',
      section: 'Getting started',
      question: 'Which GDPR roles does your organization play?',
      explanation:
        'Pick every role that applies. Most B2B SaaS companies are BOTH: a controller for their own customer, marketing, and employee data, and a processor for the personal data inside their product on behalf of customers.',
      suggestedAnswer: 'Controller + Processor is the typical B2B SaaS answer.',
      type: 'checkbox',
      required: true,
      options: [
        { value: 'ROLE_CONTROLLER', label: GDPR_SCOPE_TOKEN_LABELS.ROLE_CONTROLLER.label, description: GDPR_SCOPE_TOKEN_LABELS.ROLE_CONTROLLER.description, recommended: true },
        { value: 'ROLE_PROCESSOR', label: GDPR_SCOPE_TOKEN_LABELS.ROLE_PROCESSOR.label, description: GDPR_SCOPE_TOKEN_LABELS.ROLE_PROCESSOR.description, recommended: true },
        { value: 'ROLE_JOINT_CONTROLLER', label: GDPR_SCOPE_TOKEN_LABELS.ROLE_JOINT_CONTROLLER.label, description: GDPR_SCOPE_TOKEN_LABELS.ROLE_JOINT_CONTROLLER.description },
      ],
      skipWhen: (a) =>
        !Array.isArray(a.frameworks) || !(a.frameworks as string[]).includes('GDPR'),
    },
    {
      id: 'gdprBases',
      section: 'Getting started',
      question: 'Why does GDPR apply to you?',
      explanation:
        'The territorial basis (GDPR Art.3). Pick all that apply. This is recorded on your enrollment and shown to reviewers; it never narrows your requirements on its own.',
      suggestedAnswer: 'Offering goods or services to people in the EU is the most common basis.',
      type: 'checkbox',
      required: true,
      options: [
        { value: 'BASIS_OFFERING_TO_EU', label: GDPR_SCOPE_TOKEN_LABELS.BASIS_OFFERING_TO_EU.label, description: GDPR_SCOPE_TOKEN_LABELS.BASIS_OFFERING_TO_EU.description, recommended: true },
        { value: 'BASIS_EU_ESTABLISHMENT', label: GDPR_SCOPE_TOKEN_LABELS.BASIS_EU_ESTABLISHMENT.label, description: GDPR_SCOPE_TOKEN_LABELS.BASIS_EU_ESTABLISHMENT.description },
        { value: 'BASIS_MONITORING_EU', label: GDPR_SCOPE_TOKEN_LABELS.BASIS_MONITORING_EU.label, description: GDPR_SCOPE_TOKEN_LABELS.BASIS_MONITORING_EU.description },
      ],
      skipWhen: (a) =>
        !Array.isArray(a.frameworks) || !(a.frameworks as string[]).includes('GDPR'),
    },
    // ── Per-framework audit types ─────────────────────────────────────
    {
      id: 'auditType_SOC2',
      section: 'Getting started',
      question: 'Which SOC 2 audit type are you running?',
      explanation:
        'Type I is a point-in-time snapshot: the fastest path, 4–8 weeks. Type II observes controls over 3–12 months; stronger proof, and the one enterprise buyers ask for.',
      suggestedAnswer: "Recommended: Type II, because it's what your buyers will ask for.",
      type: 'radio',
      required: true,
      options: ONBOARDING_AUDIT_TYPE_OPTIONS.map((option) => ({
        value: option.value,
        label: option.label,
        description: option.description,
        recommended: option.value === 'TYPE_II',
      })),
      skipWhen: (a) =>
        !Array.isArray(a.frameworks) || !(a.frameworks as string[]).includes('SOC2'),
    },
    {
      id: 'hipaaEntityType',
      section: 'Getting started',
      question: 'How does HIPAA apply to your company?',
      explanation:
        "If your customers are healthcare providers, payers, or clearinghouses and you handle their patient data on their behalf, you're a Business Associate (most healthtech B2B SaaS). If you provide care directly or bill insurance (a clinic, hospital, or health plan), you're a Covered Entity, which carries heavier obligations like a Privacy Officer. Pick Hybrid only if you do both (e.g. an EHR vendor that also runs clinics).",
      suggestedAnswer: 'Most healthtech B2B SaaS is a Business Associate.',
      type: 'radio',
      required: true,
      options: ONBOARDING_HIPAA_ENTITY_TYPE_OPTIONS.map((option) => ({
        value: option.value,
        label: option.label,
        description: option.description,
        recommended: option.value === 'BUSINESS_ASSOCIATE',
      })),
      skipWhen: (a) =>
        !Array.isArray(a.frameworks) || !(a.frameworks as string[]).includes('HIPAA'),
    },
    // ── No-PHI acknowledgment (#813) ──────────────────────────────────
    // Shown only when HIPAA is selected. The workspace starts non-PHI; the
    // user must acknowledge they won't store PHI without a BAA before
    // continuing. Recorded server-side (acknowledgeNoPhi) at completion.
    {
      id: 'noPhiAcknowledgment',
      section: 'Getting started',
      question: 'Confirm how PHI is handled during setup',
      explanation:
        'This workspace is not configured to handle PHI/ePHI yet. Until a Business Associate Agreement (BAA) is executed, keep protected health information out. Use synthetic, redacted, or non-production data for evidence.',
      type: 'checkbox',
      required: true,
      options: [
        {
          value: 'acknowledged',
          label:
            'I agree not to upload, record, transmit, email, or store PHI/ePHI, patient data, medical records, or claims data in this workspace unless a Business Associate Agreement has been executed.',
        },
      ],
      skipWhen: (a) =>
        !Array.isArray(a.frameworks) || !(a.frameworks as string[]).includes('HIPAA'),
    },
    // ── Company Context ────────────────────────────────────────────────
    { id: 'companyName', section: 'Company Context', question: 'What is your company name?', explanation: 'This will appear on the audit report cover page.', type: 'text', required: true, placeholder: 'e.g. Acme Corp' },
    { id: 'companyDescription', section: 'Company Context', question: 'Describe what your company does in one or two sentences.', explanation: 'Tip: [Type] platform/tool that [does what] for [who].', type: 'textarea', required: false, placeholder: 'e.g. B2B SaaS platform that helps engineering teams manage incidents.' },
    {
      id: 'industry', section: 'Company Context', question: 'Which industry best describes your company?',
      explanation: 'Auditors use this to calibrate risk expectations and select relevant controls.',
      suggestedAnswer: 'Most companies starting a compliance program are B2B SaaS.',
      type: 'radio', required: true,
      options: ONBOARDING_INDUSTRY_OPTIONS.map((option) => ({
        value: option.value,
        label: option.label,
        description: option.description,
        recommended: option.value === 'B2B_SAAS',
      })),
    },
    {
      id: 'teamSize', section: 'Company Context', question: 'How many people are on your team?',
      explanation: 'Count everyone with a company login: full-time employees plus regular contractors. If a contractor only touches code once a quarter, leave them out.',
      type: 'radio', required: true,
      options: ONBOARDING_TEAM_SIZE_OPTIONS.map((option) => ({
        value: option.value,
        label: option.label,
        description: option.description,
        recommended: option.value === '2-10',
      })),
    },
    {
      id: 'workModel', section: 'Company Context', question: 'How does your team work?',
      explanation: 'Remote teams require written policies covering endpoint security and access management.',
      suggestedAnswer: 'Most early-stage SaaS teams are fully remote.',
      type: 'radio', required: true,
      options: ONBOARDING_WORK_MODEL_OPTIONS.map((option) => ({
        value: option.value,
        label: option.label,
        description: option.description,
        recommended: option.value === 'REMOTE',
      })),
    },
    {
      id: 'previousCompliancePlatform', section: 'Company Context', question: 'Are you switching from another compliance platform?',
      explanation: 'If you are moving off Vanta, Drata, or a similar tool, knowing what you are coming from helps us match your existing setup.',
      type: 'radio', required: false,
      options: ONBOARDING_PREVIOUS_PLATFORM_OPTIONS.map((option) => ({
        value: option.value,
        label: option.label,
        description: option.description,
        recommended: option.value === 'none',
      })),
    },
    { id: 'headquarters', section: 'Company Context', question: 'Where is your company headquartered?', explanation: 'This appears on the audit report cover page (DC 1). If fully remote, just say "Fully remote" or list your incorporation state.', suggestedAnswer: 'e.g. "San Francisco, CA" or "Fully remote (Delaware)"', type: 'text', required: false, placeholder: 'e.g. San Francisco, CA' },
    { id: 'foundingDate', section: 'Company Context', question: 'When was your company founded?', explanation: 'The founding date appears in the system description narrative (DC 1). A year is sufficient.', type: 'text', required: false, placeholder: 'e.g. 2022' },
    { id: 'founderInfo', section: 'Company Context', question: 'Who are the founders?', explanation: 'Founder names and brief backgrounds appear in the system description (DC 1). This helps auditors understand leadership context.', suggestedAnswer: 'A name and one-line background per founder is enough.', type: 'textarea', required: false, placeholder: 'e.g. Jane Smith (ex-Stripe engineering lead), John Doe (ex-AWS security)' },
    {
      id: 'dataTypes', section: 'Company Context', question: 'What types of data does your product handle?',
      explanation: 'Select all that apply. Each type adds specific control requirements (e.g. payment data needs PCI-aligned controls, health data invokes HIPAA-adjacent obligations).',
      suggestedAnswer: 'Customer PII and Employee data are typical starting points.',
      type: 'checkbox', required: false,
      options: ONBOARDING_DATA_TYPE_OPTIONS.map((option) => ({
        value: option.value,
        label: option.label,
        description: option.description,
      })),
    },
    {
      id: 'dataRegions', section: 'Company Context', question: 'In which regions is your data stored or processed?',
      explanation: 'This affects which data residency and privacy regulations apply (e.g. GDPR for Europe, PIPEDA for Canada). Auditors will ask about cross-border data flows.',
      suggestedAnswer: 'Most US-headquartered companies choose North America.',
      type: 'checkbox', required: false,
      options: [
        { value: 'NORTH_AMERICA', label: 'North America', description: 'US, Canada, Mexico' },
        { value: 'EUROPE', label: 'Europe', description: 'EU and UK, GDPR in scope' },
        { value: 'ASIA_PACIFIC', label: 'Asia-Pacific', description: 'APAC, where additional data residency laws may apply' },
      ],
    },

    // ── Infrastructure & Security (non-scanning questions) ─────────────
    // RFC-160 consolidation: databaseProviders, monitoringTools, and
    // vulnScanningTools moved to V1's "Delivery Path" section. They're all
    // scanning targets (backups/encryption, log centralization, dependency
    // findings) — kept with the other "declare + connect + scan" steps
    // after the questions section.
    {
      id: 'infrastructureModel', section: 'Infrastructure & Security', question: 'How is your infrastructure hosted?',
      explanation: `If fully cloud-hosted, physical security controls can be carved out, since your cloud provider's compliance reports cover physical access, environmental controls, and data center availability.`,
      suggestedAnswer: 'Most SaaS companies are fully cloud-hosted. If you are, physical security controls come out of scope.',
      type: 'radio', required: true,
      options: ONBOARDING_INFRASTRUCTURE_MODEL_OPTIONS.map((option) => ({
        value: option.value,
        label: option.label,
        description: option.description,
        recommended: option.value === 'cloud',
      })),
    },
    // aiProviders moved to scope (Vendors section) — LLM vendors are
    // sub-processors, grouped with other third-party data processors.
    // RFC-160: targetAuditDate dropped from the wizard — it's per-cycle,
    // not per-program. Wizard completion spawns an AuditCycle per
    // framework with dates left null; user fills them on the dashboard.

    // ── Audit Scope ────────────────────────────────────────────────────
    { id: 'productNames', section: 'Audit Scope', question: 'What products are in scope for this audit?', explanation: 'Use the names your customers see. These appear in the audit report and define the boundary of what is being certified. e.g. "Acme Cloud: production B2B SaaS for incident management." If a tool only ships to your marketing site or internal team, leave it out.', type: 'product_list', required: true },

    // RTO/RPO captured in the readiness questionnaire (IR_PHYS_AUDIT
    // section), not here — see recovery-time-objective /
    // recovery-point-objective blueprints in control-blueprint-catalog.

    // ── Compliance Roles ───────────────────────────────────────────────
    { id: 'complianceRoles', section: 'Compliance Roles', question: 'Who fills these compliance roles?', explanation: "Your policies will reference these roles, not job titles. One person can fill multiple roles (common in small teams). You can change these anytime.\n\nVera has suggested roles based on your team size and audit scope.", suggestedAnswer: 'In small teams, the CTO or founder typically fills most of these roles.', type: 'compliance_roles', required: false },

    // ── Your Auditor ───────────────────────────────────────────────────
    // RFC-160: the `hasAuditor` Yes/No gate is dropped — implicit from
    // whether the user names a firm. `targetAuditDate` + `auditPeriod`
    // moved to the dashboard's AuditCycle card.
    { id: 'auditorFirmName', section: 'Your Auditor', question: 'What is the name of your audit firm?', explanation: 'Optional. Leave blank if you haven\'t chosen one. We support common auditor formats (e.g., Prescient Assurance, Johansson Group). If your auditor provides an IRL or control matrix, we can ingest it on the next step.', type: 'text', required: false, placeholder: 'e.g. Prescient Assurance' },
    {
      id: 'hasControlMatrix', section: 'Your Auditor', question: 'Do you have a control matrix or Information Request List (IRL) from your auditor?',
      explanation: 'An IRL is a spreadsheet your auditor sends listing every control they need evidence for. If you have one, we use it to seed your controls instead of the Screenata canonical set, so nothing is missed.',
      suggestedAnswer: 'If you\'re not sure, select "Not yet". You can upload it later.',
      type: 'radio', required: false,
      options: [
        { value: 'YES', label: 'Yes, I have it', description: "I'll upload it in the next step" },
        { value: 'NO', label: 'Not yet', description: "My auditor hasn't provided one yet", recommended: true },
      ],
      skipWhen: (a) => !(a.auditorFirmName as string | undefined)?.trim(),
    },
    { id: 'controlMatrixFile', section: 'Your Auditor', question: 'Upload your control matrix or IRL', explanation: "Upload the spreadsheet your auditor sent. We accept Excel (.xlsx, .xls) and CSV files. We'll merge it with your evidence checklist after the wizard.", type: 'file', required: false, acceptedFormats: '.xlsx,.xls,.csv', skipWhen: (a) => a.hasControlMatrix !== 'YES' },

    // ── TSC + Environments (Audit Scope tail) ──────────────────────────
    {
      id: 'trustServiceCriteria', section: 'Audit Scope', question: 'Which SOC 2 trust criteria are in scope?',
      explanation:
        'Security is always included. Add any of the others that apply. The question under each one is the test.',
      // Emits the same plain `string[]` of TSC values as the legacy
      // checkbox screen, so downstream mappers and control seeding stay
      // unchanged.
      type: 'trust_criteria_picker', required: true,
      // SOC 2-specific — hide unless SOC 2 is one of the enrolled frameworks.
      skipWhen: (a) =>
        !Array.isArray(a.frameworks) || !(a.frameworks as string[]).includes('SOC2'),
      // `options` retained so the final review screen can label the selected values.
      options: [
        { value: 'Security', label: 'Security (required)', description: `Common Criteria, always included in ${frameworkName}` },
        { value: 'Availability', label: 'Availability', description: 'Uptime SLAs and business continuity' },
        { value: 'Confidentiality', label: 'Confidentiality', description: 'Protection of confidential information' },
        { value: 'Processing Integrity', label: 'Processing Integrity', description: 'Complete, valid, accurate system processing' },
        { value: 'Privacy', label: 'Privacy', description: 'Personal information collection and use' },
      ],
    },
    {
      id: 'environments', section: 'Audit Scope', question: 'Which environments will be in scope for the audit?',
      explanation: 'Only include environments that handle customer data or run production workloads. Staging is occasionally in scope if it mirrors production data. This is about deployment environments, not laptops. Device security (MDM) is scoped separately, by who can access production.',
      suggestedAnswer: "Recommended: start with production only. That's where auditors expect the tightest controls.",
      type: 'checkbox', required: false,
      options: [
        { value: 'production', label: 'Production', description: 'Live environment serving customers' },
        { value: 'staging', label: 'Staging', description: 'Pre-production environment' },
        { value: 'development', label: 'Development', description: 'Local or dev environment. Only include if it holds real customer data' },
      ],
    },
  ]
}

// ── Shared helpers ────────────────────────────────────────────────────────

export function extractCloudProviderAnswer(raw: unknown): {
  cloudProviders: string[]
  selectedServices: Record<string, string[]>
} {
  if (!raw) return { cloudProviders: [], selectedServices: {} }
  if (typeof raw === 'object' && !Array.isArray(raw) && 'providers' in (raw as object)) {
    const v = raw as CloudProviderAnswer
    return {
      cloudProviders: v.providers ?? [],
      selectedServices: v.selectedServices ?? {},
    }
  }
  if (Array.isArray(raw)) {
    return { cloudProviders: raw as string[], selectedServices: {} }
  }
  return { cloudProviders: [], selectedServices: {} }
}

/** Decode the wizard's combined compliance-goal selector value into separate framework + auditType fields. */
export function decodeComplianceGoal(goal: string): { framework: string; auditType: string } {
  switch (goal) {
    case 'SOC2_TYPE_I': return { framework: ComplianceFramework.SOC2, auditType: 'TYPE_I' }
    case 'SOC2_TYPE_II': return { framework: ComplianceFramework.SOC2, auditType: 'TYPE_II' }
    case 'HIPAA': return { framework: ComplianceFramework.HIPAA, auditType: defaultAuditTypeForFramework(ComplianceFramework.HIPAA) }
    case 'ISO27001': return { framework: ComplianceFramework.ISO27001, auditType: defaultAuditTypeForFramework(ComplianceFramework.ISO27001) }
    case 'OTHER': return { framework: ComplianceFramework.SOC2, auditType: 'TYPE_II' }
    case 'UNDECIDED': return { framework: ComplianceFramework.SOC2, auditType: 'UNDECIDED' }
    default: return { framework: ComplianceFramework.SOC2, auditType: goal }
  }
}

/** Reconstruct the wizard selector value from stored framework + auditType (inverse of decodeComplianceGoal). */
export function encodeComplianceGoal(
  framework: string | null | undefined,
  auditType: string | null | undefined,
): string {
  if (framework === ComplianceFramework.HIPAA) return ComplianceFramework.HIPAA
  if (framework === ComplianceFramework.ISO27001) return ComplianceFramework.ISO27001
  if (auditType === 'TYPE_I') return 'SOC2_TYPE_I'
  if (auditType === 'UNDECIDED') return 'UNDECIDED'
  // Stored TYPE_II, and anything absent — the selector opens on the
  // recommended default rather than on Type I.
  return 'SOC2_TYPE_II'
}

/** Default initial values for fresh wizards. */
export const DEFAULT_VALUES: Record<string, unknown> = {
}

/**
 * Can the wizard deep-link `?step=<key>` to a real screen? A key it cannot
 * resolve lands on the first unanswered screen instead, so a link offered for
 * one would quietly take the user somewhere else.
 *
 * Context screens only. Scope and connection screens are resolved separately.
 */
export function isWizardScreenKey(key: string): boolean {
  return CONTEXT_SCREEN_KEYS.has(key)
}
